import {
	AccessTime as AccessTimeIcon,
	Email as EmailIcon,
	Facebook as FacebookIcon,
	Instagram as InstagramIcon,
	LinkedIn as LinkedInIcon,
	LocationOn as LocationOnIcon,
	Phone as PhoneIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid2, IconButton, InputLabel, TextField, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import StellarviewLogo from "../../../assets/logos/stellarview-logo.png";
import { useNotification } from "../../../components/FeedbackNotification/useNotification";
import { useContactUsMutation } from "../../../redux/reducers/configuration.reducer";
import { VALIDATION_REGEX } from "../../../utils/constants";

interface IContactFormInputs {
	name: string;
	email: string;
	message: string;
}

const AboutUs: FC = () => {
	const { notify } = useNotification();

	const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

	// APIS
	// CONTACT US
	const [contactUs, { isLoading: contactUsLoading }] = useContactUsMutation();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm<IContactFormInputs>();

	const handleSubmitForm = (formData: IContactFormInputs) => {
		contactUs({
			name: formData.name,
			email: formData.email,
			message: formData.message
		})
			.unwrap()
			.then(() => {
				setIsFormSubmitted(true);
				reset();
			})
			.catch((error) => {
				notify(
					"data" in error && error.data ? String(error.data) : "Failed to send message. Please try again.",
					"error"
				);
			});
	};

	const handleResetForm = () => {
		setIsFormSubmitted(false);
		reset();
	};

	useEffect(() => {
		reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box className="page-content-wrapper">
			<Box className="page-content">
				<Box sx={{ marginBottom: 4 }}>
					<img src={StellarviewLogo} alt="Stellarview Logo" style={{ width: 200, height: "auto" }} />
				</Box>

				<Grid2 container spacing={4}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Typography variant="h5" fontWeight={500} color="var(--color-primary-dark)" marginBottom={3}>
							About Us
						</Typography>
						<Typography variant="body1" color="var(--color-text-secondary)" marginBottom={2}>
							Stellarview, a dynamic offshoot of the esteemed computer vision consulting organization Adagrad,
							represents the evolution of our journey in harnessing the power of AI and machine learning for
							transformative vision-based solutions
						</Typography>

						<Typography variant="h5" fontWeight={500} color="var(--color-primary-dark)" marginBottom={3}>
							Contact Information
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 3 }}>
							<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
								<LocationOnIcon color="primary" />
								<Box>
									<Typography variant="body1" color="var(--color-text-secondary)">
										Unit No. 9, 5th Floor
									</Typography>
									<Typography variant="body1" color="var(--color-text-secondary)">
										Clover Metropole, NIBM road,
									</Typography>
									<Typography variant="body1" color="var(--color-text-secondary)">
										Kondhwa, Pune 411048
									</Typography>
								</Box>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<PhoneIcon color="primary" />
								<Typography variant="body1" color="var(--color-text-secondary)">
									+91-8657019349
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<EmailIcon color="primary" />
								<Typography variant="body1" color="var(--color-text-secondary)">
									info@stellarview.ai
								</Typography>
							</Box>

							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<AccessTimeIcon color="primary" />
								<Typography variant="body1" color="var(--color-text-secondary)">
									Mon - Fri: 9:00 AM - 6:00 PM PST
								</Typography>
							</Box>
						</Box>

						<Typography variant="h5" fontWeight={500} color="var(--color-primary-dark)" marginBottom={2}>
							Follow Us
						</Typography>
						<Box sx={{ display: "flex", gap: 1 }}>
							<IconButton
								color="primary"
								component="a"
								href="https://www.linkedin.com"
								target="_blank"
								sx={{ backgroundColor: "rgba(25, 118, 210, 0.08)" }}
							>
								<LinkedInIcon />
							</IconButton>
							<IconButton
								color="primary"
								component="a"
								href="https://www.facebook.com"
								target="_blank"
								sx={{ backgroundColor: "rgba(25, 118, 210, 0.08)" }}
							>
								<FacebookIcon />
							</IconButton>
							<IconButton
								color="primary"
								component="a"
								href="https://www.instagram.com"
								target="_blank"
								sx={{ backgroundColor: "rgba(25, 118, 210, 0.08)" }}
							>
								<InstagramIcon />
							</IconButton>
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<Typography variant="h5" fontWeight={500} color="var(--color-primary-dark)" marginBottom={3}>
							Our Mission
						</Typography>
						<Typography variant="body1" color="var(--color-text-secondary)" marginBottom={4}>
							Stellarview's mission is to pioneer and deliver innovative vision-based vehicle analytics solutions that
							seamlessly integrate into everyday life. We are dedicated to enhancing traffic management and mobility,
							focusing on creating smarter, safer, and more efficient environments
						</Typography>

						<Typography variant="h5" fontWeight={500} color="var(--color-primary-dark)" marginBottom={3}>
							Contact Us
						</Typography>

						{isFormSubmitted ? (
							<Box sx={{ textAlign: "center", py: 4 }}>
								<Typography variant="h6" color="success.main" marginBottom={2}>
									Message Sent Successfully!
								</Typography>
								<Typography variant="body1" color="var(--color-text-secondary)" marginBottom={3}>
									Thank you for contacting us. We'll get back to you as soon as possible.
								</Typography>
								<LoadingButton variant="outlined" color="primary" onClick={handleResetForm} sx={{ mt: 2 }}>
									Send Another Message
								</LoadingButton>
							</Box>
						) : (
							<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
								<Grid2 container spacing={2}>
									<Grid2 size={12}>
										<InputLabel
											required
											htmlFor="name-input"
											error={!!errors.name}
											sx={{ fontWeight: 500, marginBottom: 1 }}
										>
											Name
										</InputLabel>
										<TextField
											required
											fullWidth
											size="small"
											variant="outlined"
											color="primary"
											id="name-input"
											placeholder="Enter your name"
											autoComplete="off"
											error={!!errors.name}
											helperText={errors.name && errors.name.message ? errors.name.message.toString() : ""}
											{...register("name", {
												required: "Name is required",
												setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
											})}
											disabled={contactUsLoading}
										/>
									</Grid2>

									<Grid2 size={12}>
										<InputLabel
											required
											htmlFor="email-input"
											error={!!errors.email}
											sx={{ fontWeight: 500, marginBottom: 1 }}
										>
											Email
										</InputLabel>
										<TextField
											required
											fullWidth
											size="small"
											variant="outlined"
											color="primary"
											id="email-input"
											type="email"
											placeholder="Enter your email"
											autoComplete="off"
											error={!!errors.email}
											helperText={errors.email && errors.email.message ? errors.email.message.toString() : ""}
											{...register("email", {
												required: "Email is required",
												pattern: { value: VALIDATION_REGEX.EMAIL, message: "Invalid email address" }
											})}
											disabled={contactUsLoading}
										/>
									</Grid2>

									<Grid2 size={12}>
										<InputLabel
											required
											htmlFor="message-input"
											error={!!errors.message}
											sx={{ fontWeight: 500, marginBottom: 1 }}
										>
											Message
										</InputLabel>
										<TextField
											required
											fullWidth
											multiline
											rows={4}
											size="small"
											variant="outlined"
											color="primary"
											id="message-input"
											placeholder="Enter your message"
											autoComplete="off"
											error={!!errors.message}
											helperText={errors.message && errors.message.message ? errors.message.message.toString() : ""}
											{...register("message", {
												required: "Message is required",
												setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
											})}
											disabled={contactUsLoading}
										/>
									</Grid2>

									<Grid2 size={12}>
										<LoadingButton
											disableElevation
											variant="contained"
											color="primary"
											type="submit"
											loading={contactUsLoading}
											disabled={contactUsLoading}
											sx={{ mt: 2 }}
										>
											Send Message
										</LoadingButton>
									</Grid2>
								</Grid2>
							</Box>
						)}
					</Grid2>
				</Grid2>
			</Box>
		</Box>
	);
};

export default AboutUs;
